import './CartItem.scss';

import cn from 'classnames';
import React from 'react';
import Dotdotdot from 'react-dotdotdot';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import Flags from '@/common/components/atoms/Flags/Flags';
import Picture from '@/common/components/atoms/Picture/Picture';
import Price from '@/common/components/blocks/Price/Price';
import Checkbox from '@/common/components/form/Checkbox/Checkbox';
import ButtonClose from '@/common/components/molecules/ButtonClose/ButtonClose';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import { useAuth } from '@/common/contexts/AuthContext';
import getImageBySize from '@/common/helpers/getImageBySize';
import withPriceAndStock from '@/common/hoc/products/withPriceAndStock';
import { ReactComponent as SwitchArrowIcon } from '@/ebat/assets/icons/arrow-switch.svg';
import Stocks from '@/ebat/components/blocks/Common/Stocks/Stocks';
import ProductAdditional from '@/ebat/components/blocks/ProductAdditional/ProductAdditional';
import {
  DEBOUNCE_DELAY_CART_QUANTITY,
  PRODUCTS_TYPES_FORMATS,
} from '@/ebat/constants';

import { CartItemDefaultProps, CartItemPropTypes } from './CartItem.proptypes';

const CartItem = ({
  agrementFgaz,
  brand,
  cartSource,
  className,
  companyAvailability,
  currentItems,
  delayQuantity,
  diff,
  disabled,
  discountClass,
  discountClasses,
  discountLabel,
  discountPriceItem,
  flags,
  // eslint-disable-next-line no-unused-vars
  hasPrice, // withPriceAndStock
  // eslint-disable-next-line no-unused-vars
  hasStock, // withPriceAndStock
  id,
  image,
  invoiceUnit,
  isDiff,
  isDiffSelected,
  isLoading,
  isSalable,
  isSelected,
  isSubstitute,
  lineSubtotal,
  // eslint-disable-next-line no-unused-vars
  mainSku, // withPriceAndStock
  manufacturerSku,
  mustSubstitute,
  name,
  onModify,
  onModifyLocal,
  onRemove,
  price,
  quantity,
  quoteNumber,
  refetchCart,
  saleUnit,
  salesPackaging,
  sku,
  source,
  splitPackaging,
  stockStatus,
  stocks,
  substitute,
  weeeAmount,
  weeeTotalAmount,
}) => {
  const { t } = useTranslation();
  const { user, zergRush } = useAuth();

  // Return related item if exists (diff or contains diffs)
  const relatedItem = () => currentItems.find((item) => item.sku === diff);

  // Test if item is selected and has diffs
  const isActive = () => {
    if (isDiff) {
      return isDiffSelected;
    }

    return !(diff && relatedItem()?.is_selected);
  };

  /**
   * Return image alt or product name
   * @returns {string}
   */
  const getImageAlt = () => image?.alt || name;

  /**
   * Return image src or empty string
   * @returns {string}
   */
  const getImageSrc = () =>
    image?.url ? getImageBySize(image.url, PRODUCTS_TYPES_FORMATS.LIST) : '';

  /**
   * Send the value if the zergRush is at false if not it passes null
   */
  const valueDiscountPriceItem = () => (!zergRush ? discountPriceItem : null);

  const valueWeeeAmount = () => (!zergRush ? weeeAmount : null);

  const valuePrice = () => (!zergRush ? price : null);

  const valueLineSubtotal = () => (!zergRush ? lineSubtotal : null);

  const valueWeeeTotalAmount = () => (!zergRush ? weeeTotalAmount : null);

  /**
   * Action on click on button "Replace" for a diff item
   * Call modify action to select/unselect items (normal/diff)
   */
  const handleReplaceProduct = () => {
    onModify({
      id: isSelected ? id : relatedItem().id,
      isSelected: false,
      quantity: isSelected ? quantity : relatedItem().quantity,
      isSelectedDiff: false,
      onSuccess: () =>
        onModify({
          id: isSelected ? relatedItem().id : id,
          isSelected: true,
          isSelectedDiff: true,
          quantity: isSelected ? quantity : relatedItem().quantity,
          onSuccess: refetchCart,
        }),
    });
  };

  const isDisabled = () => {
    const blocked = disabled || isLoading;
    const notSalable = mustSubstitute || !isSalable;
    const edit = blocked || !user?.functionality_accesses?.carts;

    return {
      delete: edit,
      disabled: blocked,
      loading: isLoading,
      manage: edit || notSalable,
      notSalable,
    };
  };

  return (
    <>
      {isActive() && (
        <Checkbox
          checked={isSelected}
          className="standalone-cart__select-item"
          context="cart-item"
          disabled={isDisabled().manage}
          id={`${id}-${sku}`}
          onChange={() =>
            onModifyLocal({
              id,
              isSelected: !isSelected,
              isSelectedDiff: !isDiffSelected,
              quantity,
            })
          }
          value={id}
        />
      )}
      <div
        className={cn('standalone-product-cart', className, {
          'standalone-product-cart--disabled': isDisabled().manage,
          'standalone-product-cart--diff': isDiff,
          'standalone-product-cart--selected': isActive(),
        })}
        data-qa-sku={sku}
        id={`standalone-product-cart-${id}`}
      >
        {isDiff && (
          <Button
            buttonSize="small"
            className="product-cart__replace"
            context="cart-item-diff-replace"
            disabled={isDisabled().manage}
            onClick={() => handleReplaceProduct()}
            variant="red-light"
            withIcon
          >
            {t('ebat:entities:cart.diff.replace')}
            <SwitchArrowIcon className="button__icon" />
          </Button>
        )}
        <Flags className="standalone-product-cart__flags" flags={flags} />
        <Picture
          alt={getImageAlt()}
          src={getImageSrc()}
          className="standalone-product-cart__image"
        />
        <h3 className="standalone-product-cart__brand" title={brand.name}>
          {brand.name}
        </h3>
        <h4
          className="standalone-product-cart__model"
          title={name}
          data-qa-id="product-cart-name"
        >
          <Dotdotdot clamp={2}>{name}</Dotdotdot>
        </h4>
        <p className="standalone-product-cart__sku">
          <span className="name">{t('common:entities:product.sku')}</span>
          {sku}
        </p>
        <p className="standalone-product-cart__reference">
          <span className="name">
            {t('common:entities:product.manufacturer_sku')}
          </span>
          {manufacturerSku}
        </p>
        <Stocks
          className="standalone-product-cart__stock"
          companyAvailability={companyAvailability}
          delayQuantity={delayQuantity}
          disabled
          origin="cart"
          status={stockStatus}
          stocks={stocks}
          unit={saleUnit}
        />
        <div className="standalone-product-cart__hr" />
        <div className="standalone-product-cart__price price">
          <p className="price__label">
            {t('common:messages:sort.customer_price')}
          </p>
          {discountPriceItem > 0 && (
            <Price
              className="price__value--discount"
              context="discount-price"
              value={valueDiscountPriceItem()}
            />
          )}
          <Price
            className={`${discountPriceItem ? 'price__value--old-price' : ''}`}
            emptyLabel={t('common:entities:product.price.not_price')}
            value={valuePrice()}
          />
          <span className="price__unit">{invoiceUnit}</span>
          {weeeAmount > 0 && (
            <span className="price__weee">
              {t('common:entities:product.price.weee_amount_long', {
                amount: (
                  <Price
                    className="amount"
                    key={`weee-amount-${valueWeeeAmount()}`}
                    sup={false}
                    value={valueWeeeAmount()}
                  />
                ),
              })}
            </span>
          )}
        </div>
        <div
          className="standalone-product-cart__total total"
          data-qa-id="product-cart-total"
        >
          <p className="total__label">
            {t('common:messages:sort.amount_total_raw')}
          </p>
          <span className="total__value">
            <Price value={valueLineSubtotal()} />
          </span>
          {weeeTotalAmount > 0 && (
            <span className="price__weee">
              {t('common:entities:product.price.weee_amount_long', {
                amount: (
                  <Price
                    className="amount"
                    key={`weee-amount-${valueWeeeTotalAmount()}`}
                    sup={false}
                    value={valueWeeeTotalAmount()}
                  />
                ),
              })}
            </span>
          )}
        </div>
        <Quantity
          className="standalone-product-cart__quantity"
          debounceDelay={DEBOUNCE_DELAY_CART_QUANTITY}
          disabled={isDisabled().manage}
          id={id}
          isLoading={isDisabled().loading}
          onChange={(data) => onModify({ ...data, isSelected })}
          quantity={quantity}
          salesPackaging={salesPackaging}
          splitPackaging={splitPackaging}
          unit={saleUnit}
        />
      </div>
      <ProductAdditional
        agrementFgaz={agrementFgaz}
        cartSource={cartSource}
        className="standalone-product-cart__additional"
        disabled={isDisabled().delete}
        discountClass={discountClass}
        discountClasses={discountClasses}
        discountLabel={discountLabel}
        id={id}
        isLoading={isDisabled().loading}
        isNotSalable={isDisabled().notSalable}
        isSubstitute={isSubstitute}
        onRemove={onRemove}
        quoteNumber={quoteNumber}
        substitute={substitute}
        source={source}
      />
      <ButtonClose
        className="standalone-cart__remove"
        disabled={isDisabled().manage}
        onClick={() => onRemove({ id })}
      />
    </>
  );
};

CartItem.defaultProps = {
  ...CartItemDefaultProps,
};

CartItem.propTypes = {
  ...CartItemPropTypes,
};

export default withPriceAndStock(CartItem);
